'use client';

import { Dropdown, DropdownItem, DropdownMenu, DropdownTrigger } from '@nextui-org/react';
import Link from 'next/link';
import React from 'react';

import { EarnAigProfileIcon, IconCopy, IconMore, IconProfileVerify, VerifyAccount } from '@/assets';
import { Buttons, Images } from '@/components';
import AvatarPlaceholder from '@/components/avatar-default';
import ButtonViewMore from '@/components/ButtonViewMore';
import ClientOnly from '@/components/client-only';
import RenderWhen from '@/components/RenderWhen';
import { configs } from '@/configs';
import useViewMore from '@/hooks/useViewMore';
import { useRouter } from '@/provider/RouterProvider';
import { useAuth } from '@/provider/UserProvider';
// import { getTwitterOauthUrl } from '@/services/provider';
import type { ProfileSocial } from '@/types';
import type { UserFollow, UserProfile } from '@/types/user';
import {
  centerTextEllipsis,
  copyClipboard,
  getIconSocial,
  getUrlDonate,
  popupCenter,
} from '@/utils';

import FollowInfo from './Follow';
import useWindowSize from '@/hooks/useWindowSize';

const UserInfomation: React.FC<{
  user: UserProfile;
  follow: UserFollow;
}> = ({ user, follow }) => {
  const { toggle, isShowBtn, isViewMore } = useViewMore(100, 'profile-desc');
  const { auth } = useAuth();
  const router = useRouter();
  const isOwner = auth.user.publicAddress === user.publicAddress;
  const { windowSize } = useWindowSize();

  const listMoreAction = [
    {
      key: 'edit',
      title: 'Edit profile',
      onClick: () => router.push(`/update-profile`),
      isShow: isOwner,
    },
    {
      key: 'privatekey',
      title: 'View private key',
      onClick: () =>
        popupCenter({
          url: `${configs.ssoUrl}/view-privatekey`,
          title: 'My private key',
          w: 500,
          h: 400,
        }),
      isShow: isOwner && !auth.isOwallet,
    },
    {
      key: 'copy',
      title: 'Copy profile link',
      onClick: (e: any) =>
        copyClipboard(e, `${window.location.host}/profile/${user.publicAddress}`),
      isShow: true,
    },
  ].filter((item) => item.isShow);

  return (
    <div>
      <div className="relative h-[320px] w-full sm:h-[200px]">
        <RenderWhen>
          <RenderWhen.If isTrue={Boolean(user.cover)}>
            <>
              <Images className="z-0 h-full w-full object-cover" src={user.cover} alt="banner" />
              <p
                style={{
                  background: 'linear-gradient(180deg, rgba(30, 30, 30, 0.00) 32.34%, #222 100%)',
                }}
                className="absolute top-0 z-10 h-full w-full"
              />
            </>
          </RenderWhen.If>
          <RenderWhen.If isTrue={Boolean(!user.cover)}>
            <>
              <Images
                className="z-0 h-full w-full object-cover"
                src="/images/defaulte_cover_profile.png"
                alt="banner"
              />
              <p
                style={{
                  background: 'linear-gradient(180deg, rgba(30, 30, 30, 0.00) 32.34%, #222 100%)',
                }}
                className="absolute top-0 z-10 h-full w-full"
              />
            </>
          </RenderWhen.If>
        </RenderWhen>
        <div className="absolute bottom-6 right-10 z-20 flex">
          <div className="flex items-center sm:hidden">
            {user.links?.map((link) => (
              <Link
                className="mx-2 flex h-[40px] w-[40px] items-center justify-center rounded-[50%] bg-gray-100 duration-300 hover:bg-gray-200 sm:mx-0 sm:mr-2"
                key={link.type}
                href={link.url}
                target="_blank"
              >
                {getIconSocial(link.type as ProfileSocial)}
              </Link>
            ))}
          </div>
          <div className="ml-2">
            <Dropdown>
              <DropdownTrigger>
                <div className="flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-[50%] bg-gray-100 duration-300 hover:bg-gray-200">
                  <IconMore />
                </div>
              </DropdownTrigger>
              <DropdownMenu aria-label="Static Actions">
                {listMoreAction.map((item) => (
                  <DropdownItem onClick={(e) => item.onClick(e)} key={item.key}>
                    {item.title}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </div>
        </div>
      </div>
      <div className="main-container">
        <div className="flex justify-between">
          <div className="relative z-[11] -mt-[150px] h-[180px] w-[180px] rounded-[50%] border-[4px] border-white bg-white shadow-[4px_2px_13px_5px_rgba(199,205,231,0.29)] sm:-mt-[90px] sm:h-[120px] sm:w-[120px]">
            <AvatarPlaceholder
              className="h-full w-full rounded-[50%] object-cover"
              src={user.picture}
              address={user.publicAddress}
              scale={20}
              decor={isOwner ? auth.user.avatarDecoration : user.avatarDecoration}
              decorScale={windowSize.width > 576 ? 114 : 80}
            />
          </div>
        </div>
      </div>
      <div className="main-container flex justify-between md:block">
        <div className="justify-between pt-7 md:block md:pt-3">
          <div>
            <div className="flex items-center">
              <div className="flex items-center flex-wrap text-[28px] font-[700] mb-2 text-[#181818] sm:line-clamp-1 sm:max-w-[180px] sm:text-[20px] ">
                {user.username || 'Unname'}{' '}
                {user.isPartner && <VerifyAccount className="ml-2 sm:ml-0 sm:scale-75" />}
                <div className="flex items-center ml-2 sm:w-fill sm:ml-0 relative rounded-full bg-blue-100 px-3 py-[6px] pl-[45px]">
                  <EarnAigProfileIcon className="scale-[0.6] absolute left-[0px] top-[50%] -translate-y-[50%]" />
                  <div className="flex flex-col">
                    <span className="text-16 font-semibold">{user.aigPoints} AIG</span>
                  </div>
                </div>
              </div>
              {user.sVerify === 'success' && (
                <span className="pl-3 md:pl-1">
                  <IconProfileVerify />
                </span>
              )}
            </div>
            <div
              onClick={(e) => copyClipboard(e, user.publicAddress)}
              className="flex cursor-pointer items-center"
            >
              <span className="mr-2 text-[14px] text-gray-500">
                {centerTextEllipsis(user.publicAddress, 10)}
              </span>
              <IconCopy />
            </div>
            <p
              id="profile-desc"
              className="mt-3 max-w-[800px] text-[16px] leading-[1.5] sm:mt-2 sm:text-[14px]"
            >
              {user.description}
            </p>
            <ClientOnly>
              <ButtonViewMore
                toggle={toggle}
                isViewMore={isViewMore}
                isShowBtn={isShowBtn}
                className="mt-3"
              />
            </ClientOnly>
          </div>
          {/* <div className="mt-5 flex flex-wrap justify-between">
            <div className="flex flex-wrap">
              <div className="mr-8">
                <div className="text-[18px] font-[600] text-[#181818]">{follow.numberOfNfts}</div>
                <p className="m-0 text-[13px] text-[#9DA3B5]">Artworks</p>
              </div>
              <div className="mr-8">
                <div className="text-[18px] font-[600] text-[#181818]">{user.totalPrompts}</div>
                <p className="m-0 text-[13px] text-[#9DA3B5]">Prompts</p>
              </div>
            </div>
          </div> */}
        </div>
        <div className="pt-7 md:pt-1">
          <div className="mt-3 hidden items-center sm:flex sm:flex-wrap">
            {user.links?.map((link) => (
              <Link
                className="mx-2 flex h-[40px] w-[40px] items-center justify-center rounded-[50%] bg-gray-100 duration-300 hover:bg-gray-200 sm:mx-0 sm:mr-2"
                key={link.type}
                href={link.url}
                target="_blank"
              >
                {getIconSocial(link.type as ProfileSocial)}
              </Link>
            ))}
          </div>
          {!isOwner && (
            <div
              style={{
                background:
                  'linear-gradient(267deg, rgb(219, 143, 255) -8.45%, rgb(255, 98, 171) 49.67%, rgb(255, 176, 144) 102.02%)',
              }}
              className="flex items-center justify-between p-4 rounded-xl mb-4"
            >
              <p className="text-[14px] text-white font-semibold">Buy Me a Coffee</p>
              <Link
                target="_blank"
                className="px-3 py-1 rounded-full bg-white text-orange-500 font-semibold text-[14px]"
                href={getUrlDonate(user.publicAddress, undefined, undefined, auth.isOwallet)}
              >
                Donate
              </Link>
            </div>
          )}
          <FollowInfo isOwner={isOwner} follow={follow} userId={user.id} />
          {isOwner && user.sVerify !== 'success' && (
            <div
              style={{
                backgroundImage: `url('/images/bg-verify-profile.png')`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
              }}
              className="mt-4 w-[270px] rounded-xl px-3 py-[15px] sm:hidden"
            >
              <div className=" flex justify-end">
                <div className="w-[180px] text-15 font-semibold leading-5 text-white">
                  Get verified and rise to the top as an AI artist today!
                </div>
              </div>
              <div className="pt-4 text-center">
                <Buttons
                  typeBtn="secondary"
                  className="w-full self-center px-[35px] py-[15px] text-15 text-orange-500"
                  onClick={() => {
                    router.push(`/update-profile#sectionVerify`);
                  }}
                >
                  <p className="text-15 font-semibold leading-5 md:text-14">Get Verified</p>
                </Buttons>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserInfomation;
